import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  BarChart2, Inbox, UserCheck, MessageSquare, CheckCircle, Clock, TrendingUp, Timer,
} from "lucide-react";

const PERIOD_OPTIONS = [
  { label: "7 dias", value: 7 },
  { label: "14 dias", value: 14 },
  { label: "30 dias", value: 30 },
  { label: "60 dias", value: 60 },
];

function StatCard({ title, value, sub, icon: Icon, color }: {
  title: string; value: number | string; sub?: string; icon: any; color: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium">{title}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className="p-2 rounded-lg bg-muted/50">
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 text-xs space-y-1">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload.map((p: any) => (
          <div key={p.name} className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-muted-foreground">{p.name}:</span>
            <span className="font-medium">{p.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

function formatMinutes(min: number | null): string {
  if (min === null || min === undefined) return "—";
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

export default function InboxReports() {
  const [days, setDays] = useState(30);

  const { data: agentReport = [] } = trpc.inboxReport.byAgent.useQuery();
  const { data: unassignedCount = 0 } = trpc.inboxReport.unassigned.useQuery();
  const { data: dailyVolume = [] } = trpc.inboxReport.daily.useQuery({ days });

  const totalConversations = agentReport.reduce((s: number, a: any) => s + a.total, 0);
  const totalOpen = agentReport.reduce((s: number, a: any) => s + a.open, 0);
  const totalClosed = agentReport.reduce((s: number, a: any) => s + a.closed, 0);
  const totalPending = agentReport.reduce((s: number, a: any) => s + a.pending, 0);
  const activeAgents = agentReport.filter((a: any) => a.total > 0).length;

  const respondingAgents = agentReport.filter((a: any) => a.avgResponseMinutes !== null);
  const avgFirstResponseMinutes = respondingAgents.length > 0
    ? Math.round(
        respondingAgents.reduce((s: number, a: any) => s + a.avgResponseMinutes, 0) /
        respondingAgents.length
      )
    : null;

  const periodOpened = dailyVolume.reduce((s: number, d: any) => s + (d.opened ?? 0), 0);
  const periodClosed = dailyVolume.reduce((s: number, d: any) => s + (d.closed ?? 0), 0);

  return (
    <PermissionGuard permission="reports:view_inbox">
      <DashboardLayout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Inbox className="h-5 w-5 text-primary" /> Relatórios de Atendimento
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Desempenho da equipe, volume de conversas e tempos de resposta
              </p>
            </div>
            <div className="flex gap-1 bg-muted/40 p-1 rounded-lg">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDays(opt.value)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    days === opt.value
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* KPIs principais */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title={`Conversas no período`}
              value={periodOpened}
              sub={`abertas em ${days} dias`}
              icon={MessageSquare}
              color="text-blue-600"
            />
            <StatCard
              title="Sem agente"
              value={unassignedCount}
              sub="aguardando atribuição"
              icon={Clock}
              color="text-amber-600"
            />
            <StatCard
              title="Resolvidas no período"
              value={periodClosed}
              sub={`encerradas em ${days} dias`}
              icon={CheckCircle}
              color="text-green-600"
            />
            <StatCard
              title="Tempo médio 1ª resposta"
              value={formatMinutes(avgFirstResponseMinutes)}
              sub="média da equipe"
              icon={Timer}
              color="text-violet-600"
            />
          </div>

          {/* KPIs secundários */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Agentes ativos"
              value={activeAgents}
              sub="com conversas atribuídas"
              icon={UserCheck}
              color="text-blue-600"
            />
            <StatCard
              title="Atribuídas (total)"
              value={totalConversations}
              sub="todas as conversas"
              icon={MessageSquare}
              color="text-purple-600"
            />
            <StatCard
              title="Abertas (total)"
              value={totalOpen}
              sub="em andamento"
              icon={Inbox}
              color="text-blue-600"
            />
            <StatCard
              title="Pendentes (total)"
              value={totalPending}
              sub="aguardando ação"
              icon={Clock}
              color="text-amber-600"
            />
          </div>

          {/* Volume diário */}
          {dailyVolume.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" /> Volume de Conversas (últimos {days} dias)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={dailyVolume} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(days / 8))} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="opened" name="Abertas" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="closed" name="Fechadas" stroke="#22c55e" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Tabela por agente */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart2 className="h-4 w-4 text-primary" /> Detalhamento por Agente
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {agentReport.length === 0 ? (
                <p className="text-sm text-muted-foreground p-6 text-center">Nenhum dado de atendimento ainda.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium text-muted-foreground">Agente</th>
                        <th className="text-center p-3 font-medium text-muted-foreground">Total</th>
                        <th className="text-center p-3 font-medium text-muted-foreground">Abertas</th>
                        <th className="text-center p-3 font-medium text-muted-foreground">Pendentes</th>
                        <th className="text-center p-3 font-medium text-muted-foreground">Fechadas</th>
                        <th className="text-center p-3 font-medium text-muted-foreground">Tempo médio 1ª resp.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(agentReport as any[]).map((agent) => (
                        <tr key={agent.agentId} className="border-b last:border-0 hover:bg-muted/20">
                          <td className="p-3">
                            <div className="font-medium">{agent.agentName}</div>
                            <div className="text-xs text-muted-foreground">{agent.agentEmail}</div>
                          </td>
                          <td className="p-3 text-center font-semibold">{agent.total}</td>
                          <td className="p-3 text-center">
                            {agent.open > 0 ? (
                              <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">{agent.open}</Badge>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="p-3 text-center">
                            {agent.pending > 0 ? (
                              <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">{agent.pending}</Badge>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="p-3 text-center">
                            {agent.closed > 0 ? (
                              <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">{agent.closed}</Badge>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="p-3 text-center text-muted-foreground text-xs">
                            {formatMinutes(agent.avgResponseMinutes)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </PermissionGuard>
  );
}
